// src/ChatWidget.jsx
import React, { useState, useEffect } from "react";

function ChatWidget() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  // ✅ Fetch suggested questions from backend
  useEffect(() => {
    const fetchSuggestions = async () => {
      try {
        const res = await fetch(
          "https://us-central1-tekuwami.cloudfunctions.net/api/suggestions"
        );
        const data = await res.json();
        if (data.questions) setSuggestions(data.questions.slice(0, 10)); // limit top 10
      } catch (error) {
        console.error("Failed to fetch suggestions:", error);
      }
    };
    fetchSuggestions();
  }, []);

  // ✅ Send message
  const sendMessage = async (questionText) => {
    const text = questionText || input;
    if (!text.trim()) return;

    const userMsg = { role: "user", content: text };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setLoading(true);

    try {
      const res = await fetch(
        "https://us-central1-tekuwami.cloudfunctions.net/api/ask",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ question: text }),
        }
      );
      const data = await res.json();

      const botMsg = {
        role: "assistant",
        content: data.answer || "Sorry, I couldn’t get an answer.",
      };
      setMessages((prev) => [...prev, botMsg]);
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: "⚠️ Error connecting to server." },
      ]);
    }

    setLoading(false);
  };

  return (
    <div
      style={{
        maxWidth: "1100px",
        margin: "3rem auto",
        padding: "2rem",
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      }}
    >
      {/* Suggested Questions */}
      <div style={{ marginBottom: "2.5rem", textAlign: "center" }}>
        <h2
          style={{
            color: "#f0c000",
            marginBottom: "1rem",
            fontSize: "1.8rem",
            fontWeight: "700",
          }}
        >
          👋 Suggested Questions
        </h2>
        <p style={{ color: "#ccc", marginBottom: "1.2rem" }}>
          Click any of these to get instant answers:
        </p>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "0.75rem",
            justifyContent: "center",
          }}
        >
          {suggestions.length > 0 ? (
            suggestions.map((q, idx) => (
              <button
                key={idx}
                onClick={() => sendMessage(q)}
                style={{
                  padding: "0.7rem 1.3rem",
                  background: "#111",
                  color: "#f0c000",
                  border: "1px solid #f0c000",
                  borderRadius: "25px",
                  cursor: "pointer",
                  fontWeight: "500",
                  transition: "all 0.2s",
                }}
                onMouseOver={(e) => {
                  e.target.style.background = "#f0c000";
                  e.target.style.color = "#111";
                }}
                onMouseOut={(e) => {
                  e.target.style.background = "#111";
                  e.target.style.color = "#f0c000";
                }}
              >
                {q}
              </button>
            ))
          ) : (
            <p style={{ color: "#777" }}>Loading suggestions...</p>
          )}
        </div>
      </div>

      {/* Chatbox */}
      <div
        style={{
          border: "2px solid #f0c000",
          padding: "1.5rem",
          borderRadius: "16px",
          background: "#0b0b0b",
          color: "#fff",
          boxShadow: "0px 6px 18px rgba(0,0,0,0.6)",
        }}
      >
        <h2
          style={{
            color: "#f0c000",
            textAlign: "center",
            marginBottom: "1rem",
            fontSize: "1.6rem",
          }}
        >
          Tekuwami Assistant 💬
        </h2>

        <div
          style={{
            maxHeight: 500,
            overflowY: "auto",
            marginBottom: "1rem",
            padding: "1rem",
            background: "#1a1a1a",
            borderRadius: "12px",
          }}
        >
          {messages.map((msg, idx) => (
            <div
              key={idx}
              style={{
                display: "flex",
                marginBottom: "0.8rem",
                justifyContent:
                  msg.role === "user" ? "flex-end" : "flex-start",
              }}
            >
              {msg.role === "assistant" && (
                <div
                  style={{
                    width: "35px",
                    height: "35px",
                    borderRadius: "50%",
                    background: "#f0c000",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: "bold",
                    marginRight: "0.6rem",
                  }}
                >
                  A
                </div>
              )}

              <div
                style={{
                  padding: "0.85rem 1.1rem",
                  borderRadius: "12px",
                  maxWidth: "70%",
                  background:
                    msg.role === "user" ? "#f0c000" : "#333",
                  color: msg.role === "user" ? "#111" : "#fff",
                  fontSize: "1rem",
                  lineHeight: "1.4",
                  boxShadow: "0px 3px 6px rgba(0,0,0,0.4)",
                }}
              >
                {msg.content}
              </div>

              {msg.role === "user" && (
                <div
                  style={{
                    width: "35px",
                    height: "35px",
                    borderRadius: "50%",
                    background: "#0f0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: "bold",
                    marginLeft: "0.6rem",
                  }}
                >
                  U
                </div>
              )}
            </div>
          ))}
          {loading && (
            <div style={{ color: "#aaa", fontStyle: "italic" }}>
              Assistant is typing...
            </div>
          )}
        </div>

        {/* Input */}
        <div style={{ display: "flex" }}>
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            style={{
              flex: 1,
              padding: "1rem",
              borderRadius: "10px",
              border: "1px solid #444",
              background: "#1a1a1a",
              color: "#fff",
              fontSize: "1rem",
            }}
            placeholder="Type your question..."
          />
          <button
            onClick={() => sendMessage()}
            style={{
              marginLeft: "0.7rem",
              padding: "1rem 1.6rem",
              background: "#f0c000",
              border: "none",
              borderRadius: "10px",
              fontWeight: "bold",
              cursor: "pointer",
              fontSize: "1rem",
              transition: "all 0.2s",
            }}
          >
            Send
          </button>
        </div>
      </div>
    </div>
  );
}

export default ChatWidget;
